import { supabase } from '../lib/supabaseClient';
import type { AiEval, AiFeedbackType, AiProposal, AiProposalActionType, AiResponseEnvelope, AiRun, AiToolCall, UUID } from '../types/domain';

/**
 * AI Analyst (FASE 14, migration 0055; corrected in migration 0056).
 *
 * PROVIDER STATUS: there is no real LLM provider credential (Anthropic/
 * OpenAI/etc.) configured anywhere in this application's environment. Every
 * answer here comes from the real internal DEV_PROVIDER — a deterministic
 * keyword→intent→tool rule engine, NOT a language model — classified
 * ARCHITECTURE_PROVEN / DEV_PROVIDER_PROVEN. It composes its answer purely
 * from real ai_tool_calls results (tardiness, stock_balances, purchase
 * orders, journal_lines, metric_registry, report runs); it never invents a
 * number, and states DATA NOT AVAILABLE rather than guessing. Real
 * production LLM-backed generation is BLOCKED_BY_PROVIDER.
 *
 * AI never writes ERP data directly. A mutation-intent question only ever
 * produces a PENDING ai_proposals row — execution happens exclusively via
 * fn_ai_approve_proposal_checked, which re-validates the REAL domain
 * permission for the specific action_type and then calls an already-
 * accepted canonical domain function (fn_open_maintenance_order_checked /
 * fn_create_purchase_request). Every `_checked` RPC below independently
 * re-validates the caller's real ERP permission — AI tool/approval scope
 * cannot exceed the calling user's own scope.
 *
 * RESPONSE CONTRACT: `fn_ai_query_checked` and `fn_ai_brief_checked` both
 * return the literal `AiResponseEnvelope` shape (migration 0056) — not
 * just an id you then fetch pieces of separately. It is a pure
 * read-projection over ai_runs/ai_tool_calls/ai_proposals for one
 * ai_run_id (fn_ai_build_envelope, server-side); the envelope is never a
 * second source of truth.
 */

// ---------------------------------------------------------------------------
// Query (ai.query)
// ---------------------------------------------------------------------------

/**
 * idempotencyKey: generate ONCE per logical question submission and reuse
 * the SAME key on a retry of that exact submission — the server resolves a
 * retry to the already-created canonical ai_run, never a duplicate run.
 * Returns the full structured envelope directly (answer/confidence/
 * evidence/warnings/proposals/data_timestamp/scope/tool_refs/ai_run_id).
 */
export async function askAi(params: {
  branchId: UUID | null;
  question: string;
  hints?: Record<string, unknown>;
  idempotencyKey: UUID;
}): Promise<AiResponseEnvelope> {
  const { data, error } = await supabase.rpc('fn_ai_query_checked', {
    p_branch_id: params.branchId,
    p_question: params.question,
    p_hints: params.hints ?? {},
    p_idempotency_key: params.idempotencyKey,
  });
  if (error) throw new Error(`fn_ai_query_checked failed: ${error.message}`);
  return data as AiResponseEnvelope;
}

/**
 * Daily Owner Brief (on-demand only — automatic daily scheduling is
 * BLOCKED_BY_INFRASTRUCTURE, no cron/worker exists in this build). Reuses
 * the exact same `ai.query` permission as askAi() — a brief is not a new
 * permission concept, just a composed question, so it cannot bypass
 * existing AI permission/RLS/branch/tenant isolation. Each section
 * (attendance, open PO, branch stock, finance P&L) independently
 * re-checks the real caller permission via the same dispatcher askAi()
 * uses; a denied/unavailable section is stated honestly, never fabricated.
 */
export async function askAiBrief(params: { branchId: UUID | null; idempotencyKey: UUID }): Promise<AiResponseEnvelope> {
  const { data, error } = await supabase.rpc('fn_ai_brief_checked', {
    p_branch_id: params.branchId,
    p_idempotency_key: params.idempotencyKey,
  });
  if (error) throw new Error(`fn_ai_brief_checked failed: ${error.message}`);
  return data as AiResponseEnvelope;
}

/** Re-fetch the envelope for an existing run (e.g. after creating a proposal against it, to see envelope.proposals[] updated). */
export async function getAiResponseEnvelope(aiRunId: UUID): Promise<AiResponseEnvelope> {
  const { data, error } = await supabase.rpc('fn_ai_get_response_envelope_checked', { p_ai_run_id: aiRunId });
  if (error) throw new Error(`fn_ai_get_response_envelope_checked failed: ${error.message}`);
  return data as AiResponseEnvelope;
}

export async function listAiRuns(): Promise<AiRun[]> {
  const { data, error } = await supabase
    .from('ai_runs')
    .select(
      'ai_run_id, tenant_id, branch_id, user_id, provider, model, prompt_version, intent, question_text, idempotency_key, started_at, completed_at, status, tool_call_count, data_timestamp, result_summary, result_confidence, denial_reason, request_id'
    )
    .order('started_at', { ascending: false })
    .limit(100);
  if (error) throw new Error(`listAiRuns failed: ${error.message}`);
  return (data ?? []) as AiRun[];
}

export async function getAiToolCalls(aiRunId: UUID): Promise<AiToolCall[]> {
  const { data, error } = await supabase.rpc('fn_ai_get_tool_calls_checked', { p_ai_run_id: aiRunId });
  if (error) throw new Error(`fn_ai_get_tool_calls_checked failed: ${error.message}`);
  return (data ?? []) as AiToolCall[];
}

// ---------------------------------------------------------------------------
// Proposals (ai.approve) — proposal creation never executes anything; only
// approval does, and only for MAINTENANCE_ORDER_SUGGESTION / REORDER_SUGGESTION.
// ---------------------------------------------------------------------------

export async function proposeAiAction(params: {
  aiRunId: UUID;
  actionType: AiProposalActionType;
  targetResourceType: string;
  targetResourceId?: UUID | null;
  reason: string;
  evidenceRefs?: unknown[];
  proposedParameters?: Record<string, unknown>;
  idempotencyKey: UUID;
}): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_ai_propose_action_checked', {
    p_ai_run_id: params.aiRunId,
    p_action_type: params.actionType,
    p_target_resource_type: params.targetResourceType,
    p_target_resource_id: params.targetResourceId ?? null,
    p_reason: params.reason,
    p_evidence_refs: params.evidenceRefs ?? [],
    p_proposed_parameters: params.proposedParameters ?? {},
    p_idempotency_key: params.idempotencyKey,
  });
  if (error) throw new Error(`fn_ai_propose_action_checked failed: ${error.message}`);
  return data as UUID;
}

export async function listAiProposals(): Promise<AiProposal[]> {
  const { data, error } = await supabase
    .from('ai_proposals')
    .select(
      'proposal_id, ai_run_id, tenant_id, branch_id, requested_by, action_type, target_resource_type, target_resource_id, reason, evidence_refs, proposed_parameters, status, idempotency_key, created_at, decided_by, decided_at, decision_reason, executed_at, executed_reference, expires_at'
    )
    .order('created_at', { ascending: false })
    .limit(100);
  if (error) throw new Error(`listAiProposals failed: ${error.message}`);
  return (data ?? []) as AiProposal[];
}

/**
 * ai.approve alone is NOT sufficient — the server re-checks the real domain
 * permission for the specific action_type (e.g. maintenance_order.create,
 * purchase_request.create) before executing. A REJECT never touches domain
 * data. An APPROVE that fails a downstream domain constraint is recorded
 * durably as EXECUTION_FAILED (with the real error in decision_reason) —
 * this call can still return successfully even though execution failed, so
 * always re-fetch the proposal after approving to see the real outcome.
 */
export async function decideAiProposal(proposalId: UUID, decision: 'APPROVE' | 'REJECT', reason: string): Promise<void> {
  const { error } = await supabase.rpc('fn_ai_approve_proposal_checked', {
    p_proposal_id: proposalId,
    p_decision: decision,
    p_decision_reason: reason,
  });
  if (error) throw new Error(`fn_ai_approve_proposal_checked failed: ${error.message}`);
}

// ---------------------------------------------------------------------------
// Feedback (ai.feedback) — never alters ai_runs/source data, purely evaluative.
// ---------------------------------------------------------------------------

export async function submitAiFeedback(aiRunId: UUID, feedbackType: AiFeedbackType, comment?: string): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_ai_submit_feedback_checked', {
    p_ai_run_id: aiRunId,
    p_feedback_type: feedbackType,
    p_comment: comment ?? null,
  });
  if (error) throw new Error(`fn_ai_submit_feedback_checked failed: ${error.message}`);
  return data as UUID;
}

// ---------------------------------------------------------------------------
// Eval suite (ai.read) — read-only visibility into the pass/fail scenarios
// this build's safety guarantees were live-tested against.
// ---------------------------------------------------------------------------

export async function listAiEvals(): Promise<AiEval[]> {
  const { data, error } = await supabase
    .from('ai_evals')
    .select('eval_id, eval_code, category, description, scenario, expected_outcome, last_status, last_checked_at, last_ai_run_id, created_at')
    .order('eval_code', { ascending: true });
  if (error) throw new Error(`listAiEvals failed: ${error.message}`);
  return (data ?? []) as AiEval[];
}
